import { PrinterOutlined } from '@ant-design/icons';
import { GuestUserType } from '@kinderlabs-pos/shared-data-type';
import { OrderState } from '@kinderlabs-pos/state';
import { List, WithDialog } from '@kinderlabs-pos/ui-atoms';
import { ExpandButton, useConfirm } from '@kinderlabs-pos/ui-components';
import { Button, Collapse, Divider, Stack, StackProps, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Fragment, useEffect, useState } from 'react';
import { MappingTicketQuery } from '../../../mappingTicket/mappingTicketQuery';
import { PrintItemDisplay } from '../common/PrintItemDisplay';
import { StyledProgress } from '../common/StyledProgress';
import { VaunceDdiziMappingDialog } from '../VaunceDdiziMappingDialog';
import { VaunceDdiziPrintEventHandler } from './VaunceDdiziPrintEventHandler';
import { WaiverApis } from '@kinderlabs-pos/apis/waivers';
import {
	VaunceAppV2Api,
	바운스오더QR코드찍으면있어야할데이터,
} from '@kinderlabs-pos/feature/pos/vaunce-integration';
import { getUuidV4 } from '@kinderlabs-pos/shared-util';

export const VaunceDdiziPrintHandler = ({
	orderId,
	storeId,
	users,
	guestVisitId,
	StackProps,
}: {
	orderId: string;
	storeId: number;
	guestVisitId?: number;
	users: GuestUserType[];
	StackProps?: StackProps;
}) => {
	const confirm = useConfirm();
	const [출력시작됨, set출력시작됨] = useState(false);
	const dizzisWithStatus = useAtomValue(OrderState.printing.ddizis.printing);
	const { expanded, toggleExpandButton } = ExpandButton.useToggle(true);
	const [바운스오더QR코드맵, set바운스오더QR코드맵] = useAtom(바운스오더QR코드찍으면있어야할데이터);

	const isPaused = VaunceDdiziPrintEventHandler.useIsPaused();
	const pausePrinting = VaunceDdiziPrintEventHandler.usePause();
	const resumePrinting = VaunceDdiziPrintEventHandler.useResume();

	const completeLength = dizzisWithStatus.filter(
		(ddizi) => ddizi.printStatus === 'COMPLETE'
	).length;
	const { data: mappingTicketData } = useQuery({
		...MappingTicketQuery.query.mappingTicket.getTicketMappingInfoForOrders(orderId),
		suspense: false,
	});

	const 최초매핑데이터 = useQuery({
		queryKey: ['최초매핑데이터', guestVisitId],
		queryFn: async () => {
			if (!guestVisitId) return [];

			const gv = await WaiverApis.getGuestVisitDetail({ guestVisitId });
			if (!gv.appStoreVisitId) return [];

			return (
				바운스오더QR코드맵[gv.id]?.map((member) => ({
					tempUuid: getUuidV4(),
					userName: member.username,
					userBirth: member.birthday,
					userGender: member.gender as 'MALE' | 'FEMALE',
					//추가로 고려
					ticketId: member.ticketId,
					packageChildId: member.packageChildId,
					packageChildTicketName: member.packageChildTicketName,
					onlineTicketId: member.onlineTicketId,
					ticketName: member.ticketName,
				})) ?? []
			);
		},
	});

	const createMemberBulkMutation = MappingTicketQuery.useCreateMemberBulk({});
	useEffect(() => {
		if (최초매핑데이터.data && 최초매핑데이터.data.length > 0) {
			createMemberBulkMutation
				.mutateAsync({
					storeId,
					orderId,
					ticketList: dizzisWithStatus
						.filter((ddizi) => ddizi.type !== 'PARENTS')
						.map((ddizi) => ({
							id: ddizi.id,
							label: ddizi.name,
							minutes: ddizi.minutes,
							published: ddizi.publishInfo.published,
							ticketType: ddizi.type,
							available: ddizi.available,
						})),
					memberList: 최초매핑데이터.data.map((member) => ({
						tempUuid: member.tempUuid,
						userName: member.userName,
						userBirth: member.userBirth,
						userGender: member.userGender,
					})),
					initialMemberTicketMapping: 최초매핑데이터.data.reduce((acc, curr) => {
						if (curr.packageChildId) {
							// 패키지 상품
							const ddiziFound = dizzisWithStatus.find(
								(ddizi) =>
									ddizi.publishInfo.sourceId === `${curr.ticketId}` &&
									ddizi.name === curr.packageChildTicketName
							);
							if (ddiziFound) acc[ddiziFound.id] = curr.tempUuid;
						} else if (curr.onlineTicketId) {
							// 온라인 티켓
							const ddiziFound = dizzisWithStatus.find(
								(ddizi) => ddizi.publishInfo.sourceId === curr.onlineTicketId
							);
							if (ddiziFound) acc[ddiziFound.id] = curr.tempUuid;
						} else {
							const ddiziFound = dizzisWithStatus.find(
								(ddizi) => ddizi.publishInfo.sourceId === `${curr.ticketId}`
							);
							if (ddiziFound) acc[ddiziFound.id] = curr.tempUuid;
						}
						return acc;
					}, {} as Record<string, string>),
				})
				.then(() => {
					set바운스오더QR코드맵((prev) => {
						if (!guestVisitId) return prev;
						const newMap = { ...prev };
						delete newMap[guestVisitId];
						return newMap;
					});
				});
		}
	}, [최초매핑데이터.data]);

	const isComplete = dizzisWithStatus.length > 0 && completeLength === dizzisWithStatus.length;
	return (
		// eslint-disable-next-line react/jsx-no-useless-fragment
		<>
			{dizzisWithStatus.length > 0 && (
				<Stack {...StackProps}>
					<VaunceDdiziPrintEventHandler
						deviceType='POS'
						orderId={orderId}
					/>
					<List
						sx={{ width: '100%' }}
						disablePadding>
						<List.ItemButton
							onClick={toggleExpandButton}
							sx={{ position: 'relative', width: '100%', justifyContent: 'space-between' }}>
							<StyledProgress
								isPaused={isPaused}
								variant='determinate'
								value={(100 * completeLength) / dizzisWithStatus.length}
							/>
							<Typography variant={'h4'}>
								{`바운스 티켓 출력 ${
									isComplete ? '완료' : isPaused ? '중지' : '중'
								} (${completeLength} / ${dizzisWithStatus.length})`}
							</Typography>
							<Stack
								spacing={1}
								direction={'row'}>
								{!출력시작됨 && (
									<WithDialog
										dialog={(open, closeDialog) => (
											<VaunceDdiziMappingDialog
												open={open}
												closeDialog={closeDialog}
												orderId={orderId}
												ddizis={dizzisWithStatus}
												users={users}
											/>
										)}>
										{(openDialog) => (
											<Button
												variant={'outlined'}
												onClick={(e) => {
													e.stopPropagation();
													openDialog();
												}}>
												고객 매핑
											</Button>
										)}
									</WithDialog>
								)}
								{!출력시작됨 && (
									<Button
										variant={'outlined'}
										onClick={(e) => {
											e.stopPropagation();

											confirm(`출력 시작시 더이상 고객 매핑 수정이 불가능합니다.`, async () => {
												set출력시작됨(true);
												resumePrinting();
											});
										}}>
										매핑완료 & 출력시작
									</Button>
								)}
								{출력시작됨 &&
									!isComplete &&
									(!isPaused ? (
										<Button
											color={'success'}
											variant={'outlined'}
											onClick={(e) => {
												e.stopPropagation();
												pausePrinting();
											}}>
											중지
										</Button>
									) : (
										<Button
											color={'error'}
											startIcon={<PrinterOutlined />}
											variant={'outlined'}
											onClick={(e) => {
												e.stopPropagation();
												resumePrinting();
											}}>
											재개
										</Button>
									))}
								{출력시작됨 && isComplete && (
									<Button
										color={'success'}
										sx={{ pointerEvents: 'none' }}
										variant={'outlined'}>
										출력 완료
									</Button>
								)}
								<ExpandButton
									expanded={expanded}
									onClick={(e) => {
										e.stopPropagation();
										toggleExpandButton();
									}}
								/>
							</Stack>
						</List.ItemButton>
					</List>
					<Stack sx={{ overflowY: 'auto', flex: 1, width: '100%' }}>
						<Collapse in={expanded}>
							<List
								sx={{ width: '100%' }}
								disablePadding>
								{dizzisWithStatus.map((ddizi, idx) => {
									const mappingMember = mappingTicketData?.find(
										(item) => item.mappedTicketId === ddizi.id
									);
									return (
										<Fragment key={idx}>
											<List.Item sx={{ width: '100%', height: 40 }}>
												<PrintItemDisplay
													label={`${ddizi.name} / #${ddizi.id} / ${mappingMember?.name ?? ''}`}
													status={ddizi.printStatus}
													resumePrinting={resumePrinting}
												/>
											</List.Item>
											<Divider />
										</Fragment>
									);
								})}
							</List>
						</Collapse>
					</Stack>
				</Stack>
			)}
		</>
	);
};
